import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { shiftApi } from '@/services/api';
import { X, Loader2, DollarSign } from 'lucide-react';
import toast from 'react-hot-toast';
import DenominationCounter from './DenominationCounter';
import ShiftSummaryCards from './ShiftSummaryCards';

import { useQuery } from '@tanstack/react-query';
import { getApiErrorMessage } from '@/utils/apiError';

import { formatMoney } from '@/utils/currency';

type Props = {
  shift: any;
  onClose: () => void;
  onSuccess: () => void;
};

export default function ResettleShiftModal({ shift, onClose, onSuccess }: Props) {
  const qc = useQueryClient();

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['shiftSummary', shift?.name],
    queryFn: async () => {
      if (!shift?.name) return null;
      const res = await shiftApi.getSummary(shift.name);
      return res.data.message;
    },
    enabled: !!shift?.name,
    staleTime: 0,
  });

  
  const [closingAmount, setClosingAmount] = useState('');
  const [cashHandover, setCashHandover] = useState('');
  const [remarks, setRemarks] = useState('');
  const [showDenominations, setShowDenominations] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: {
      shift: string;
      closing_amount: number;
      cash_handover: number;
      remarks?: string;
    }) => shiftApi.resettleRejectedShift(data),
    onSuccess: () => {
      toast.success('Shift submitted for review');
      qc.invalidateQueries({ queryKey: ['myShifts'] });
      onSuccess();
    },
    onError: (err: any) => {
      toast.error(getApiErrorMessage(err, 'Failed to resettle shift'));
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!closingAmount) {
      toast.error('Please enter the closing amount');
      return;
    }
    
    mutation.mutate({
      shift: shift.name,
      closing_amount: parseFloat(closingAmount),
      cash_handover: parseFloat(cashHandover) || 0,
      remarks,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-fade-in flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Resettle Shift</h3>
            <p className="text-sm text-gray-500 font-mono">{shift.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
          <form id="resettle-form" onSubmit={handleSubmit} className="space-y-5">
            <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm border border-red-100">
              <p className="font-semibold mb-1">Shift Rejected</p>
              <p>Your previous count was rejected by a manager. Please carefully recount your cash drawer and submit the new amount.</p>
            </div>

            {summaryLoading ? (
              <div className="flex items-center gap-2 text-gray-500 py-4 justify-center">
                <Loader2 className="w-5 h-5 animate-spin" /> Loading full shift summary...
              </div>
            ) : (
              <ShiftSummaryCards summary={summary} openingAmount={shift.opening_amount || 0} />
            )}

            {/* Keep the Variance/Previous count as a quick recap since it's highly relevant to resettle */}
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-red-700 font-medium mb-1">Expected Cash in Drawer</p>
                <p className="font-semibold text-red-900">{formatMoney(summary?.expected_cash ?? shift.expected_cash ?? 0)}</p>
                <p className="text-[11px] text-red-500 mt-0.5">Opening + Sales + Collections − Returns</p>
              </div>
              <div>
                <p className="text-xs text-red-700 font-medium mb-1">Previous Count</p>
                <p className="font-semibold text-red-900">{formatMoney(shift.closing_amount || 0)}</p>
              </div>
              <div>
                <p className="text-xs text-red-700 font-medium mb-1">Variance</p>
                <p className={`font-semibold ${(shift.difference || 0) < 0 ? 'text-red-700' : 'text-red-900'}`}>
                  {formatMoney(shift.difference || 0)}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Counted Cash Amount
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    className="input-field pl-10 text-lg"
                    value={closingAmount}
                    onChange={e => setClosingAmount(e.target.value)}
                    placeholder="0.00"
                  />
                  <button
                    type="button"
                    onClick={() => setShowDenominations(!showDenominations)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Use Calculator
                  </button>
                </div>
              </div>

              {showDenominations && (
                <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                  <DenominationCounter 
                    onTotalChange={(total) => setClosingAmount(total.toString())}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cash Handover (Optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input-field"
                  value={cashHandover}
                  onChange={e => setCashHandover(e.target.value)}
                  placeholder="Amount handed over"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks
                </label>
                <textarea
                  className="input-field min-h-[80px]"
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                  placeholder="Explain the recount..."
                />
              </div>
            </div>
          </form>
        </div>

        <div className="p-4 border-t border-gray-100 bg-gray-50 flex gap-3 shrink-0">
          <button
            type="submit"
            form="resettle-form"
            disabled={mutation.isPending}
            className="flex-1 btn-primary py-2.5 flex items-center justify-center gap-2"
          >
            {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Submit Recount
          </button>
          <button type="button" onClick={onClose} className="btn-secondary py-2.5 px-6">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
